import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { BLOG_POSTS } from "@/lib/data";
import { Seo } from "@/components/Seo";

const FADE_UP = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8 },
};

export default function Blog() {
    const [featured, ...rest] = BLOG_POSTS;

    return (
        <main data-testid="blog-page" className="bg-bone">
            <Seo
                title="Blog — Building Advice from the Central Coast | Brazier Building & Design"
                description="Honest building advice from the Central Coast — quotes, budgets, timelines and lessons from 30+ years on the tools, by licensed builder Justin Brazier."
                path="/blog"
            />
            {/* Masthead */}
            <section className="relative pt-20 pb-10 md:pt-28 md:pb-14 bg-bone2">
                <div className="container-wide">
                    <div className="overline mb-5">The Journal</div>
                    <h1 className="font-display text-5xl md:text-7xl font-light tracking-tight leading-[1.05] max-w-4xl">
                        Notes from <span className="italic text-clay">the build.</span>
                    </h1>
                    <p className="mt-6 max-w-xl text-ink2 text-base md:text-lg">
                        Honest advice, stories and lessons from 30+ years of building on the Central Coast.
                    </p>
                </div>
            </section>

            {/* Featured story — magazine spread */}
            <section className="section-pad">
                <div className="container-wide">
                    <motion.article {...FADE_UP}>
                        <Link
                            to={`/blog/${featured.slug}`}
                            data-testid={`blog-card-${featured.slug}`}
                            className="group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center"
                        >
                            <div className="lg:col-span-7 overflow-hidden bg-bone2">
                                <div className="aspect-[4/3]">
                                    <img
                                        src={featured.cover}
                                        alt={featured.coverAlt}
                                        className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                                    />
                                </div>
                            </div>
                            <div className="lg:col-span-5">
                                <div className="flex items-center gap-3 text-[11px] tracking-[0.25em] uppercase">
                                    <span className="text-clay font-medium">{featured.category}</span>
                                    <span className="w-6 h-px bg-line" />
                                    <span className="text-ink2">{featured.date}</span>
                                </div>
                                <h2 className="mt-5 font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.1]">
                                    {featured.title}
                                </h2>
                                <p className="mt-3 font-display italic text-clay text-lg md:text-xl font-light">
                                    {featured.subtitle}
                                </p>
                                <p className="mt-5 text-ink2 leading-relaxed max-w-md">
                                    {featured.excerpt}
                                </p>
                                <div className="mt-8 inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-medium text-ink group-hover:text-clay transition-colors">
                                    Read the story
                                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                                </div>
                            </div>
                        </Link>
                    </motion.article>

                    {/* More stories */}
                    {rest.length > 0 && (
                        <div className="mt-20 md:mt-28">
                            <div className="flex items-center gap-4 mb-10">
                                <span className="w-10 h-px bg-clay" />
                                <h3 className="overline">More stories</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                                {rest.map((p) => (
                                    <Link
                                        key={p.slug}
                                        to={`/blog/${p.slug}`}
                                        data-testid={`blog-card-${p.slug}`}
                                        className="group"
                                    >
                                        <div className="aspect-[4/3] overflow-hidden bg-bone2">
                                            <img
                                                src={p.cover}
                                                alt={p.coverAlt}
                                                loading="lazy"
                                                className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                                            />
                                        </div>
                                        <div className="mt-4 text-[11px] tracking-[0.25em] uppercase text-clay">{p.category}</div>
                                        <h4 className="mt-2 font-display text-xl md:text-2xl font-light tracking-tight group-hover:text-clay transition-colors">
                                            {p.title}
                                        </h4>
                                        <div className="mt-2 text-xs text-ink2">{p.date} · {p.readTime}</div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </main>
    );
}
